import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Grid, Paper, Typography, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Breadcrumbs, CircularProgress, Chip, Card, CardContent
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { ReceiptLong as ReceiptLongIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const INVOICE_STATUS_COLORS: any = {
    PAID: { bg: '#E6FFFA', text: '#38A169' },
    PARTIALLY_PAID: { bg: '#FFFAF0', text: '#DD6B20' },
    UNPAID: { bg: '#FFF5F5', text: '#E53E3E' },
    OVERDUE: { bg: '#742A2A', text: '#FFFFFF' },
};

const ParentFinancePage = () => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    const fetchOverview = useCallback(async () => {
        try {
            setLoading(true);
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl('parents/overview'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setData(res.data);
        } catch (error) {
            console.error('Fetch parent overview error:', error);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOverview();
    }, [fetchOverview]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>;

    if (!data || !data.students) return (
        <Box sx={{ p: 4, textAlign: 'center', mt: 10 }}>
            <Typography variant="h5" color="text.secondary">Guardian Profile Not Linked</Typography>
            <Typography color="text.secondary">This portal is for parents with students enrolled at the school.</Typography>
        </Box>
    );

    return (
        <Box sx={{ p: 4, bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography variant="h4" fontWeight={800} color="#263B7C">Finance & Billing</Typography>
                    <Typography variant="body2" color="text.secondary">Track your student's fee payments and balances</Typography>
                </Box>
                <Breadcrumbs separator="/" aria-label="breadcrumb">
                    <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>Portal</Typography>
                    <Typography color="#F9B43F" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Finance</Typography>
                </Breadcrumbs>
            </Stack>

            <Grid container spacing={4}>
                {/* Total Balance Card */}
                <Grid size={12}>
                    <Card sx={{ borderRadius: '16px', bgcolor: '#263B7C', color: 'white', p: 1 }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>TOTAL OUTSTANDING BALANCE</Typography>
                                    <Typography variant="h3" fontWeight={800}>${Number(data.householdBalance).toLocaleString()}</Typography>
                                </Box>
                                <AccountBalanceWalletIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Individual Students */}
                {data.students.map((student: any) => (
                    <Grid size={12} key={student.id}>
                        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', p: 3 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                                <Box>
                                    <Typography variant="h6" fontWeight={700} color="#263B7C">{student.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">Student ID: {student.studentId}</Typography>
                                </Box>
                                <Chip
                                    label={`Balance: $${Number(student.balance).toLocaleString()}`}
                                    color={student.balance > 0 ? "error" : "success"}
                                    variant="outlined"
                                    sx={{ fontWeight: 700 }}
                                />
                            </Stack>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600 }}>INVOICE</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>TERM</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>DATE</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>DESC</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>AMOUNT</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>PAID</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>STATUS</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {student.invoices.map((inv: any) => (
                                            <TableRow key={inv.id}>
                                                <TableCell sx={{ fontWeight: 600 }}>{inv.invoiceNo}</TableCell>
                                                <TableCell>{inv.term}</TableCell>
                                                <TableCell>{new Date(inv.createdAt).toLocaleDateString()}</TableCell>
                                                <TableCell>{inv.items[0]?.description || 'School Fees'}</TableCell>
                                                <TableCell>${Number(inv.totalAmount).toLocaleString()}</TableCell>
                                                <TableCell>${inv.receipts.reduce((sum: number, r: any) => sum + Number(r.amount), 0).toLocaleString()}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={inv.status}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: INVOICE_STATUS_COLORS[inv.status]?.bg,
                                                            color: INVOICE_STATUS_COLORS[inv.status]?.text,
                                                            fontWeight: 700,
                                                            fontSize: '0.65rem'
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                ))}

                {data.students.length === 0 && (
                    <Grid size={12}>
                        <Box sx={{ p: 5, textAlign: 'center', opacity: 0.5 }}>
                            <ReceiptLongIcon sx={{ fontSize: 64, mb: 2 }} />
                            <Typography>No billing records found for your students.</Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default ParentFinancePage;
